import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { Button } from '@scio3/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { useOptibotStore } from '../stores/optibot-store';
import { DeviceTroubleshootSessionView } from './DeviceTroubleshootSessionView';
import {
  useDeviceTroubleshootSessions,
  useDeviceTroubleshootTranscript,
  useFetchTroubleshootTranscript,
  useResumeDeviceTroubleshootSession,
} from '../hooks/useDeviceTroubleshootHistory';
import type { SessionEvent } from '../hooks/useDeviceSessionStream';

/** The roomy surface for a single session, shown in place of the chat when one is focused. Live sessions
 *  open the SSE + show the steer composer; history sessions replay the recorded transcript (read-only).
 *  The chat thread is preserved underneath — "Back to chat" just clears the focus. */
export function FocusedSessionPane() {
  const { t } = useTranslation();
  const focused = useOptibotStore((s) => s.focusedSession);
  const closeSession = useOptibotStore((s) => s.closeSession);
  const openSession = useOptibotStore((s) => s.openSession);
  const { isTeamAdmin, isRootOwner } = usePermissions();
  const canResume = isTeamAdmin || isRootOwner; // re-attach re-mints a live token → write-capable roles only

  // Re-attach a STILL-LIVE session that was opened as history. The OptiBot history list always opens history
  // (no token), and an in-chat card falls back to history after a reload once its in-memory conn is gone — so
  // a session that's actually still running would render as a stale "Ended" transcript (the reported bug). The
  // runner keeps running independent of the browser, so when the server still reports the session running/
  // creating we RESUME the SAME session — device-agent-api reconnects the live session and re-mints a token
  // WITHOUT booting a new runner (active._id === sid) — and flip to the live stream. We do NOT use start():
  // opening a NEW session on a device that already has a live one is refused (409). One attempt per focused
  // session; on failure we fall back to the read-only history.
  const { sessions, loading: sessionsLoading } = useDeviceTroubleshootSessions();
  const { resume } = useResumeDeviceTroubleshootSession();
  const reconnectTried = useRef<Set<string>>(new Set());
  const [reconnecting, setReconnecting] = useState(false);
  // A failed auto re-attach (resume threw OR returned no handle) while the server STILL reports the session
  // live must NOT drop to the stale read-only "Ended" history — surface a Retry (mirrors the MDM panel's D2
  // fix). `reconnectNonce` re-arms the one-shot effect when the user clicks Retry.
  const [reconnectFailedIds, setReconnectFailedIds] = useState<Set<string>>(new Set());
  const [reconnectNonce, setReconnectNonce] = useState(0);

  const focusedId = focused?.session.sessionId ?? null;
  const row = focusedId ? sessions.find((s) => s.sessionId === focusedId) : undefined;
  const isLiveStatus = row?.status === 'running' || row?.status === 'creating';
  const reconnectFailed = !!focusedId && reconnectFailedIds.has(focusedId);
  const shouldReattach =
    !!focused &&
    focused.mode === 'history' &&
    canResume &&
    isLiveStatus &&
    !!row?.deviceUUID &&
    !!focusedId &&
    !reconnectTried.current.has(focusedId);

  useEffect(() => {
    if (!shouldReattach || !focused || !row?.deviceUUID) return;
    const sid = focused.session.sessionId;
    reconnectTried.current.add(sid);
    setReconnecting(true);
    void (async () => {
      try {
        const handle = await resume(sid, row.deviceUUID);
        if (handle) {
          openSession({ session: handle, mode: 'live', deviceLabel: focused.deviceLabel });
          setReconnectFailedIds((prev) => {
            if (!prev.has(handle.sessionId)) return prev;
            const next = new Set(prev);
            next.delete(handle.sessionId);
            return next;
          });
        } else {
          // resume returned no handle → a failed re-attach: the server still reports this live, so offer Retry
          // instead of silently dropping to the stale "Ended" history.
          setReconnectFailedIds((prev) => new Set(prev).add(sid));
        }
      } catch {
        // Transient resume error — the session is still running on the runner. Offer Retry, not stale Ended.
        setReconnectFailedIds((prev) => new Set(prev).add(sid));
      } finally {
        setReconnecting(false);
      }
    })();
  }, [shouldReattach, focused, row?.deviceUUID, resume, openSession, reconnectNonce]);

  // Hooks must run unconditionally — load the transcript only for history mode (skips when id is null).
  const historyId = focused?.mode === 'history' ? focused.session.sessionId : null;
  const { events, loading, error } = useDeviceTroubleshootTranscript(historyId);

  // A LIVE (incl. resumed/reattached) session renders as ONE continuous thread — seed the view with the FULL
  // multi-life transcript (prior conversation + new), same as the MDM panel. Fetched imperatively once per
  // focused live session; the view seeds initialEvents ONCE on mount, so we gate the live mount on the seed
  // being ready (below). The seeded prior `session.ended` shows the resume boundary and never flips to Ended.
  const fetchFullTranscript = useFetchTroubleshootTranscript();
  const liveId = focused?.mode === 'live' ? focused.session.sessionId : null;
  const [liveSeed, setLiveSeed] = useState<{ id: string; events: SessionEvent[] } | null>(null);
  useEffect(() => {
    if (!liveId || liveSeed?.id === liveId) return; // fetch once per focused live session
    let cancelled = false;
    void fetchFullTranscript(liveId).then((ev) => {
      if (!cancelled) setLiveSeed({ id: liveId, events: ev });
    });
    return () => {
      cancelled = true;
    };
  }, [liveId, liveSeed?.id, fetchFullTranscript]);

  if (!focused) return null;
  const isHistory = focused.mode === 'history';
  // The view seeds its event log from initialEvents ONCE on mount — so for history we must wait for the
  // transcript to finish loading before mounting it, or the view stays empty ("Connecting to the device…").
  const waitingForTranscript = isHistory && loading && events.length === 0;
  // Live view seeds initialEvents once on mount — hold the mount until the full-history seed for THIS session
  // is fetched (like history above), so a resumed session opens as the continuous thread, not just the new life.
  const liveSeedReady = liveSeed?.id === focused.session.sessionId;
  const waitingForLiveSeed = focused.mode === 'live' && !liveSeedReady;
  // While (re)attaching a still-live session — OR while the fresh status list is still loading (network-only,
  // no cache) so we don't yet know whether it's live — show a "Reconnecting…" state, never a stale Ended flash.
  const showReconnecting =
    reconnecting || shouldReattach || (focused.mode === 'history' && canResume && sessionsLoading);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center border-b border-border px-2 py-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={closeSession}
          aria-label={t('optibot.session.backToChat')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('optibot.session.backToChat')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-2">
        {showReconnecting || waitingForLiveSeed ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('optibot.session.conn.reconnecting')}
          </div>
        ) : reconnectFailed && isLiveStatus ? (
          // The one auto re-attach failed but the SERVER still reports this session live — it is NOT ended
          // history. Offer a manual Retry (re-arms the one-shot) instead of a stale "Ended" view.
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
            <span>{t('optibot.session.conn.reconnectFailed')}</span>
            <Button
              size="md"
              variant="outline"
              data-testid="reconnect-retry"
              onClick={() => {
                if (!focusedId) return;
                reconnectTried.current.delete(focusedId); // re-arm the one-shot…
                setReconnectFailedIds((prev) => {
                  const next = new Set(prev);
                  next.delete(focusedId);
                  return next;
                }); // …drop the failed flag…
                setReconnectNonce((n) => n + 1); // …and bump the nonce → the reconnect effect re-fires
              }}
            >
              <Play className="mr-1 h-4 w-4" />
              {t('optibot.session.conn.retry')}
            </Button>
          </div>
        ) : waitingForTranscript ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('optibot.session.history.loading')}
          </div>
        ) : isHistory && error ? (
          <div className="p-4 text-xs text-destructive">
            {t('optibot.session.history.loadError')}
          </div>
        ) : (
          <DeviceTroubleshootSessionView
            session={focused.session}
            variant="fullpage"
            mode={focused.mode}
            deviceLabel={focused.deviceLabel}
            // history → the recorded transcript; live → the full multi-life seed (continuous thread), guarded
            // ready above. The stream dedups by seq, so the floored SSE only appends events past the seed.
            initialEvents={isHistory ? events : liveSeedReady ? liveSeed.events : []}
          />
        )}
      </div>
    </div>
  );
}
